import { TextField } from '@fs/zion-ui'
import React, { useCallback, useState, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { css } from '@emotion/core'
import _ from 'lodash'
import { getUserWithUsername, getUser } from '../../services/userService'
import { UserLink } from '../app/Links'
import { getEntity } from '../../util/EntityUtil'
import callEachUntilSuccess from '../../util/FunctionUtil'

const smallLink = css`
  margin-inline-start: 5px !important;
  margin-bottom: 2px !important;
  color: colors.green40;
`

const userErrorMessageCss = css`
  color: colors.red40;
`
const userMessageCss = css`
  color: colors.green40;
`
export default function UserPicker({ userNameOrId, onUserChanged, disabled, ...props }) {
  const { t } = useTranslation()
  const [userMessage, setUserMessage] = useState()
  const [userErrorMessage, setUserErrorMessage] = useState()
  const [userNameOrIdParam, setUserNameOrId] = useState(userNameOrId)

  // Necessary to store the most current state value because the useCallback functions do not get them
  const currentUserNameOrId = useRef(userNameOrIdParam)

  useEffect(() => {
    currentUserNameOrId.current = userNameOrIdParam
  }, [userNameOrIdParam])

  const processUser = useCallback(
    (user) => {
      if (user) {
        if (user.identity.users[0].accountType && user.identity.users[0].accountType === 'NONMEMBER') {
          setUserErrorMessage(
            <span css={[smallLink, userErrorMessageCss]}>{t('user.error.nonMember', 'User is non-member')}</span>
          )
        } else if (user.identity.users[0].type && user.identity.users[0].type === 'wellKnownContributorAccount') {
          setUserErrorMessage(
            <span css={[smallLink, userErrorMessageCss]}>{t('user.error.wkc', 'User is Well-known contributor')}</span>
          )
        } else {
          setUserErrorMessage(null)
          setUserMessage(
            <UserLink
              newTab
              userId={user.contributorId}
              userName={user.identity?.users?.[0].username}
              showId
              spaceId
              css={userMessageCss}
            />
          )
          onUserChanged(user)
        }
      } else {
        setUserErrorMessage(
          <span css={[smallLink, userErrorMessageCss]}>{t('user.error.notFound', 'User not found')}</span>
        )
      }
    },
    [onUserChanged, t]
  )

  const findUser = useCallback(
    (inputValue) => {
      setUserErrorMessage(null)
      setUserMessage(null)
      if (inputValue) {
        const id = getEntity(inputValue)
        let promise
        if (id.numericId > 0) {
          promise = callEachUntilSuccess([
            () => getUser(inputValue, 'contributorId'),
            () => getUserWithUsername(inputValue),
          ])
        } else {
          promise = getUserWithUsername(inputValue)
        }

        promise
          .then((results) => {
            // In case previous results return after more recent results
            if (results && inputValue === currentUserNameOrId.current) {
              processUser(Array.isArray(results.data) ? results.data[0] : results.data)
            }
          })
          .catch(() => {
            if (inputValue === currentUserNameOrId.current) {
              setUserErrorMessage(
                <span css={[smallLink, userErrorMessageCss]}>{t('user.error.notFound', 'User not found')}</span>
              )
            }
          })
      }
    },
    [processUser, t]
  )

  useEffect(() => {
    if (userNameOrId) {
      findUser(userNameOrId)
    }
  }, [findUser, userNameOrId])

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const findUserDebounced = useCallback(
    _.debounce((inputValue) => {
      findUser(inputValue)
    }, 500),
    []
  )

  const onUserNameChange = (e) => {
    const inputValue = e.target.value
    setUserNameOrId(inputValue)
    findUserDebounced(inputValue)
  }

  return (
    <TextField
      {...props}
      value={userNameOrIdParam}
      onChange={onUserNameChange}
      autoComplete="on"
      placeholder={t('user.input.usernameOrId', 'Username or user ID')}
      validMessage={userMessage}
      errorMessage={userErrorMessage}
    />
  )
}
